import React, { useEffect, useState } from 'react';

import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useGlobalFilterDataStore } from 'store/globalFilterData.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationSetSubProgramPreferenceProgressScurve } from 'api/mutations/setPrefferedSPIMethod.mutation';
import { useMutationSetSubProgramPreferencePartialCompletion } from 'api/mutations/setSubProgramPreferencePartialCompletion.mutation';
import { getPivotTableKey } from 'api/queries/getPivotTable.query';

import {
    ProgressMeasurement,
    options as progressMeasurementOptions,
} from 'components/common/GlobalFiltersInPage/components/progressMeasurementFilter/progressMeasurementFilter';

import { getDecodedValue } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramProgressOverview/programProgressOverview.utils';
import { Select } from 'components/DesignSystem/Select';
import { ConfirmSaveModal } from 'components/common';
import subProgramPreferencesClasses from 'components/widgets/SubProgramPreferences/SubProgramPreferences.module.scss';
import classes from 'components/widgets/SubProgramPreferences/components/progressMeasurement/components/progressMeasurementPreference/progressMeasurementPreference.module.scss';
import { useProgressMeasurement } from 'components/widgets/SubProgramPreferences/components/progressMeasurement/components/progressMeasurementPreference/useProgressMeasurementPreference';

export const ProgressMeasurementPreference = () => {
    const { contract } = useProjectTreeStore();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<ISelectOption<ProgressMeasurement> | null>(null);
    const { handleSnackBar } = useCustomSnackBar();
    const { value: progressMeasurementValue, refetch } = useProgressMeasurement();
    const { update } = useGlobalFilterDataStore();
    const queryClient = useQueryClient();

    const { mutateAsync: mutateProgressScurve, isLoading: mutateLoadingProgressScurve } =
        useMutationSetSubProgramPreferenceProgressScurve();
    const { mutateAsync: mutatePartialCompletion, isLoading: mutateLoadingPartialCompletion } =
        useMutationSetSubProgramPreferencePartialCompletion();

    useEffect(() => {
        if (progressMeasurementValue) {
            const value = progressMeasurementOptions.find((item) => item.value === progressMeasurementValue) || null;
            if (value) {
                setSelectedValue(value);
            }
        }
    }, [progressMeasurementValue]);

    const currentValue = progressMeasurementOptions.find((item) => item.value === progressMeasurementValue) || null;

    const handleChange = (value: ISelectOption<ProgressMeasurement> | null) => {
        setShowConfirmModal(true);
        setSelectedValue(value);
    };

    const handleHideModal = () => {
        setShowConfirmModal(false);
        setSelectedValue(currentValue);
    };

    const handleSave = () => {
        setShowConfirmModal(false);
        if (selectedValue && contract) {
            const value = getDecodedValue(selectedValue.value);
            Promise.all([
                mutateProgressScurve({
                    subProgramId: contract.id,
                    option: value.progressMeasurement,
                }),
                mutatePartialCompletion({
                    subProgramId: contract.id,
                    option: value.partialCompletionCredit,
                }),
            ])
                .then(() => {
                    queryClient.removeQueries(getPivotTableKey);
                    update('progressMeasurement', selectedValue.value); // update global filter store with new value
                    handleSnackBar('Progress Measurement has changed', 'success');
                })
                .catch((error: QueryError) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                })
                .finally(() => {
                    refetch();
                });
        }
    };

    return (
        <div className={classes.progressMeasurementPreference}>
            <span className={subProgramPreferencesClasses.title}>Preferred Method:</span>
            <div>
                <Select<ISelectOption<ProgressMeasurement>>
                    value={selectedValue}
                    options={progressMeasurementOptions}
                    onChange={handleChange}
                    isClearable={false}
                />
            </div>
            <ConfirmSaveModal
                isShowModal={showConfirmModal}
                onHideModal={handleHideModal}
                onConfirm={handleSave}
                isLoading={mutateLoadingProgressScurve || mutateLoadingPartialCompletion}
                title={'Confirm changes'}
            >
                You have made changes. Would you like to save them?
            </ConfirmSaveModal>
        </div>
    );
};
